import { useId } from 'react';

/**
 * Large, phone-friendly numeric input with decrement/increment buttons and
 * native number-input arrows (type="number" + step/min).
 * Value is kept as a string so parent parsing stays explicit.
 */
export interface NumberStepperProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  step: number;
  min?: number;
  suffix?: string;
  hint?: string;
  decimals?: number;
}

/** Pure nudge used by ± buttons — exported for deterministic step tests. */
export function nudgeStepperValue(
  value: string,
  step: number,
  dir: 1 | -1,
  options: { min?: number; decimals?: number } = {},
): string {
  const min = options.min ?? 0;
  const decimals = options.decimals ?? 2;
  const current = Number(value);
  const base = Number.isFinite(current) ? current : 0;
  const next = Math.max(min, base + dir * step);
  return next.toFixed(decimals);
}

export function NumberStepper({
  label,
  value,
  onChange,
  step,
  min = 0,
  suffix,
  hint,
  decimals = 2,
}: NumberStepperProps): JSX.Element {
  const id = useId();

  const nudge = (dir: 1 | -1): void => {
    onChange(nudgeStepperValue(value, step, dir, { min, decimals }));
  };

  return (
    <div className="field" style={{ margin: 0 }}>
      <label htmlFor={id}>{label}</label>
      <div className="stepper">
        <button
          type="button"
          className="btn stepper-btn"
          onClick={() => nudge(-1)}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <div className="stepper-input">
          <input
            id={id}
            type="number"
            inputMode="decimal"
            step={step}
            min={min}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
          />
          {suffix && <span className="stepper-suffix">{suffix}</span>}
        </div>
        <button
          type="button"
          className="btn stepper-btn"
          onClick={() => nudge(1)}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}
